// @ts-nocheck — proton-engine types don't match its actual JS API
import React from 'react';
import Proton from 'proton-engine';
import RAFManager from 'raf-manager';

interface State {
  opacity: number;
}

export default class ParticlesAttract_v2 extends React.Component<{}, State> {
  private proton: any;
  private emitter: any;
  private renderer: any;
  private containerRef = React.createRef<HTMLDivElement>();
  private canvasRef = React.createRef<HTMLCanvasElement>();
  private resizeHandler!: () => void;
  private rafId: number | null = null;

  constructor(props: {}) {
    super(props);
    this.state = { opacity: 0 };
    this.renderProton = this.renderProton.bind(this);
  }

  componentDidMount() {
    // Fade in after first paint
    this.rafId = requestAnimationFrame(() => {
      this.setState({ opacity: 1 });
    });

    this.initCanvas();
    this.resizeHandler = this.handleResize.bind(this);
    window.addEventListener('resize', this.resizeHandler);
  }

  componentWillUnmount() {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    window.removeEventListener('resize', this.resizeHandler);
    try {
      RAFManager.remove(this.renderProton);
      this.proton.destroy();
    } catch (e) {}
  }

  getCanvasDimensions() {
    const container = this.containerRef.current;
    if (!container) return { width: 400, height: 400 };
    const rect = container.getBoundingClientRect();
    return { width: rect.width, height: rect.height };
  }

  initCanvas() {
    const canvas = this.canvasRef.current;
    if (!canvas) return;

    const { width, height } = this.getCanvasDimensions();
    canvas.width = width;
    canvas.height = height;

    this.createProton(canvas, width, height);
    RAFManager.add(this.renderProton);
  }

  createProton(canvas: HTMLCanvasElement, width: number, height: number) {
    const proton = new Proton();
    const emitter = new Proton.Emitter();

    emitter.rate = new Proton.Rate(new Proton.Span(38), new Proton.Span(0.05, 0.2));
    emitter.addInitialize(new Proton.Mass(100));
    emitter.addInitialize(new Proton.Radius(1, 5));
    emitter.addInitialize(new Proton.Life(Infinity));

    const pointZone = new Proton.Position(
      new Proton.RectZone(0, 0, width, height)
    );
    emitter.addInitialize(pointZone);
    emitter.addInitialize(
      new Proton.Velocity(new Proton.Span(0.15, 0.3), new Proton.Span(0, 360), 'polar')
    );

    emitter.addBehaviour(new Proton.Alpha(Proton.getSpan(0.6, 1.0)));
    emitter.addBehaviour(new Proton.Color('#ffffff'));
    emitter.addBehaviour(
      new Proton.CrossZone(new Proton.RectZone(0, 0, width, height), 'cross')
    );

    emitter.emit('once');
    emitter.damping = 0;
    proton.addEmitter(emitter);

    const renderer = this.createRenderer(canvas);
    proton.addRenderer(renderer);

    setTimeout(() => {
      emitter.rate = new Proton.Rate(new Proton.Span(1), 1.5);
      emitter.removeInitialize(pointZone);
    }, 0);

    this.proton = proton;
    this.emitter = emitter;
    this.renderer = renderer;
  }

  createRenderer(canvas: HTMLCanvasElement) {
    const R = 100;
    const context = canvas.getContext('2d')!;
    const renderer = new Proton.CanvasRenderer(canvas);

    renderer.onProtonUpdateAfter = () => {
      const particles = this.emitter.particles;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const pA = particles[i];
          const pB = particles[j];
          const dis = pA.p.distanceTo(pB.p);
          if (dis < R) {
            const alpha = (1 - dis / R) * 0.15;
            context.strokeStyle = `rgba(255,255,255,${alpha})`;
            context.beginPath();
            context.moveTo(pA.p.x, pA.p.y);
            context.lineTo(pB.p.x, pB.p.y);
            context.closePath();
            context.stroke();
          }
        }
      }
    };

    return renderer;
  }

  handleResize() {
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    const { width, height } = this.getCanvasDimensions();
    canvas.width = width;
    canvas.height = height;
    this.renderer?.resize(width, height);
  }

  handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!this.emitter?.particles?.length) return;
    for (let i = 0; i < 3; i++) {
      if (this.emitter.particles[i]) this.emitter.particles[i].dead = true;
    }
    const x = e.nativeEvent.offsetX;
    const y = e.nativeEvent.offsetY;
    setTimeout(() => {
      this.emitter.p.x = x;
      this.emitter.p.y = y;
      this.emitter.emit('once');
    }, 60);
  };

  handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!this.emitter?.particles?.length) return;
    const ease = 0.3;
    const p0 = this.emitter.particles[0];
    if (!p0) return;
    const x = e.nativeEvent.offsetX;
    const y = e.nativeEvent.offsetY;
    p0.p.x += (x - p0.p.x) * ease;
    p0.p.y += (y - p0.p.y) * ease;
    p0.radius = 0;
  };

  handleMouseUp = () => {
    this.emitter?.stop();
  };

  renderProton() {
    this.proton?.update();
  }

  render() {
    return (
      <div
        ref={this.containerRef}
        style={{
          position: 'fixed',
          right: 30,
          top: 140,
          bottom: 190,
          width: 400,
          opacity: this.state.opacity,
          transition: 'opacity 0.6s ease',
          pointerEvents: 'auto',
          cursor: 'crosshair',
        }}
      >
        <canvas
          ref={this.canvasRef}
          onMouseDown={this.handleMouseDown}
          onMouseMove={this.handleMouseMove}
          onMouseUp={this.handleMouseUp}
          style={{ display: 'block', width: '100%', height: '100%' }}
        />
      </div>
    );
  }
}
